// Cloudflare Access bypass warning.
//
// Published views are served under the `/~/` path and are meant to be PUBLICLY
// reachable (a grid / form / menu / export / API link anyone can open). If the
// deployment sits behind Cloudflare Access WITHOUT a bypass rule for `/~/`,
// Access intercepts those paths too: an anonymous visitor is stopped at the
// Access login screen and never sees the published view.
//
// We detect this by probing `/~/_meta/access`. That endpoint lives on the very
// path published views use, and reports whether the request reached the Worker
// still carrying Cloudflare Access's injected headers — which only happens when
// `/~/` is NOT bypassed. When it is detected, we surface a one-time explanatory
// dialog (dismissal remembered) so the admin knows to add the bypass rule.

import { isCloudAvailable } from "../local/mode.js";
import { alertDialog } from "./dialogs.js";

const DISMISS_KEY = "csvdb.accessBypassWarn.dismissed";

export async function maybeWarnAccessBypass() {
  // Only meaningful on a real Worker deployment — local / in-browser mode has
  // no Cloudflare Access in front of it.
  if (!isCloudAvailable()) return;
  try {
    if (localStorage.getItem(DISMISS_KEY) === "1") return;
  } catch { /* storage blocked — fall through and show it anyway */ }

  // The probe has THREE meaningful outcomes, not two:
  //   a) JSON { accessOnPublicPath:false } → healthy: the Worker ran and Access
  //      let the request through (bypass in place). Nothing to warn about.
  //   b) JSON { accessOnPublicPath:true }  → Access is NOT bypassed on `/~/`.
  //   c) NON-JSON (HTML) response          → the request fell through to the
  //      SPA's static-asset fallback, i.e. the Worker did NOT run first for
  //      `/~/`. Published links serve index.html instead of the viewer, and
  //      `run_worker_first` is missing/stale on the deployment.
  //
  // Case (c) is the dangerous blind spot: the OLD probe did `res.json()` inside
  // a try/catch and silently returned on the parse error, so a broken `/~/`
  // route produced NO warning — indistinguishable from "all good". We now
  // detect it explicitly and warn, because it's the more common production
  // misconfiguration (forgot to redeploy after adding `/~/*`).
  let res;
  try {
    res = await fetch("/~/_meta/access", { headers: { accept: "application/json" }, cache: "no-store" });
  } catch { return; } // network error — say nothing (could be offline).
  if (!res.ok) return;

  const ctype = res.headers.get("content-type") || "";
  if (!ctype.includes("application/json")) {
    // Case (c): the Worker isn't intercepting `/~/` — the SPA shell came back.
    await alertDialog({
      title: "Published views are not being served by the Worker",
      message:
        "A request to the “/~/” path (used by every published view) was answered by the app shell instead of the Worker — so public grid, form, menu, export and API links return the full app page instead of the viewer, and won’t work for anonymous visitors.\n\n" +
        "This means the Worker is not running before static-asset matching for “/~/”. Check that `run_worker_first` lists “/~/*” in your wrangler config and redeploy (a config change only takes effect after `wrangler deploy`).",
    });
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* storage blocked */ }
    return;
  }

  let info;
  try { info = await res.json(); } catch { return; }
  // accessOnPublicPath === true ⇒ Access processed a /~/ request (no bypass).
  if (!info || !info.accessOnPublicPath) return;

  await alertDialog({
    title: "Published views need an Access bypass",
    message:
      "This deployment is protected by Cloudflare Access, and that rule also covers the “/~/” path used by published views.\n\n" +
      "As a result, anyone opening a public grid, form, menu, export or API link will be stopped at the Cloudflare Access login screen instead of seeing the published view.\n\n" +
      "To make published views publicly accessible, add a Bypass policy for the path “/~/*” to your Cloudflare Access application " +
      "(Zero Trust → Access → Applications). The rest of the app stays protected.",
  });
  // Remember dismissal so the dialog doesn't reappear every boot. Persisted only
  // AFTER the user closes it, so a missed/instant dismissal still re-warns.
  try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* storage blocked */ }
}
